/**
 * ViewContent/AddToCart server-side (Meta Conversions API) pela ponte do
 * próprio app `vtex.facebook-capi`.
 *
 * **Por que existe:** quem manda evento pro `vtex.facebook-capi` é o pixel do
 * FBE (`vtex.facebook-fbe`), que só ouve os eventos do pixel-manager do Store
 * Framework (`vtex:productView`, `vtex:addToCart`, `vtex:orderPlaced`). Na
 * vitrine (este worker, headless) esses eventos não existem — Purchase e
 * InitiateCheckout chegam (orderPlaced/checkout são páginas da VTEX), mas
 * ViewContent/AddToCart iam SÓ pelo Pixel do navegador (GTM-N7CX5NT).
 *
 * **Como funciona:** monta o mesmo corpo que o FbePixel manda pro app
 * (`{eventName, email, payload, eventId, eventSourceURL, userAgent,
 * fbcCookie, fbpCookie}`) e faz `sendBeacon` pra rota própria
 * `POST /tracking/meta-capi` (src/routes/tracking/meta-capi.ts). Lá o worker
 * acrescenta `external_id` (que o contrato do app da VTEX não tem), IP e
 * cookies da requisição e manda direto pra Graph API; sem token ou com erro,
 * repassa o corpo intacto pra ponte `/_v/facebook-capi/event` do app — o
 * comportamento anterior. Ver src/server/metaCapi.ts.
 *
 * **Dedup:** `eventId` = o `event_id` que `sendEvent`/`SEND_EVENT_JS` já
 * põem no push (PR #182). As tags "AT | FB - ViewContent/AddToCart" do GTM
 * passam esse mesmo campo como `eventID` (versão 166 do container), então
 * navegador e servidor casam na Meta. Push sem `event_id` é ignorado: sem
 * id compartilhado, mandar o server-side só duplicaria a contagem.
 *
 * Chamado de dentro do wrapper de `window.dataLayer.push`
 * (src/sdk/navTracking.ts) — mesmo motivo do navTracking: os `view_item`
 * saem de 3 mecanismos diferentes, incluindo o script inline em string de
 * `SendEventOnLoad`, que não importa nada.
 *
 * Gate de consentimento: envia a menos que o visitante tenha recusado no banner (decisão de
 * produto, mesma postura do CRM).
 */
import { hasCookieConsent } from "~/utils/consent";
import { getCookie } from "~/utils/cookies";

export const FACEBOOK_CAPI_EVENT_URL = "/tracking/meta-capi";

/**
 * Chave que o próprio FBE grava no checkout com o e-mail do cliente (e que
 * `~/sdk/metaUserData` grava pro cliente logado na vitrine).
 */
export const FBE_EMAIL_STORAGE_KEY = "fbeEventUserEmail";

interface AnalyticsItemLike {
  item_id?: unknown;
  item_name?: unknown;
  price?: unknown;
  quantity?: unknown;
}

interface DataLayerPushArg {
  event?: unknown;
  event_id?: unknown;
  ecommerce?: { items?: AnalyticsItemLike[]; currency?: unknown } | null;
}

interface MetaContent {
  id: string;
  quantity: number;
  item_price?: number;
}

export interface FacebookCapiEventBody {
  eventName: "ViewContent" | "AddToCart";
  email?: string;
  payload: Record<string, unknown>;
  eventId: string;
  eventSourceURL: string;
  userAgent: string;
  fbcCookie?: string;
  fbpCookie?: string;
}

function toContents(items: AnalyticsItemLike[]): MetaContent[] {
  const contents: MetaContent[] = [];
  for (const item of items) {
    if (!item || typeof item.item_id !== "string" || !item.item_id) continue;
    contents.push({
      id: item.item_id,
      quantity: typeof item.quantity === "number" && item.quantity > 0 ? item.quantity : 1,
      item_price: typeof item.price === "number" && Number.isFinite(item.price)
        ? item.price
        : undefined,
    });
  }
  return contents;
}

/**
 * Pura (testável): monta o `payload` no MESMO formato que o FbePixel usa
 * pra cada evento, ou `null` se o push não for coberto / não tiver item /
 * não tiver `event_id`. Na vitrine `item_id` já é o SKU (mesmo id do
 * catálogo do FBE e das tags do GTM).
 */
export function buildFacebookCapiEvent(
  arg: unknown,
  ctx: Pick<FacebookCapiEventBody, "eventSourceURL" | "userAgent" | "email" | "fbcCookie" | "fbpCookie">,
): FacebookCapiEventBody | null {
  if (!arg || typeof arg !== "object") return null;
  const push = arg as DataLayerPushArg;
  if (push.event !== "view_item" && push.event !== "add_to_cart") return null;
  if (typeof push.event_id !== "string" || !push.event_id) return null;

  const items = push.ecommerce?.items;
  if (!Array.isArray(items)) return null;
  const contents = toContents(items);
  if (contents.length === 0) return null;

  const currency = typeof push.ecommerce?.currency === "string" ? push.ecommerce.currency : "BRL";
  const value = Number(
    contents.reduce((sum, c) => sum + (c.item_price ?? 0) * c.quantity, 0).toFixed(2),
  );
  const contentIds = contents.map((c) => c.id);

  const payload: Record<string, unknown> = push.event === "view_item"
    ? {
      content_ids: contentIds,
      content_name: typeof items[0]?.item_name === "string" ? items[0].item_name : undefined,
      content_type: "product",
      currency,
      value,
    }
    : { value, content_ids: contentIds, contents, content_type: "product", currency };

  return {
    eventName: push.event === "view_item" ? "ViewContent" : "AddToCart",
    email: ctx.email,
    payload,
    eventId: push.event_id,
    eventSourceURL: ctx.eventSourceURL,
    userAgent: ctx.userAgent,
    fbcCookie: ctx.fbcCookie,
    fbpCookie: ctx.fbpCookie,
  };
}

/** E-mail que o FBE já guardou (só se vier inteiro — o orderForm mascara com "*"). */
function readFbeEmail(): string | undefined {
  try {
    const email = localStorage.getItem(FBE_EMAIL_STORAGE_KEY);
    return email && email.includes("@") && !email.includes("*") ? email : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Pushes já encaminhados. WeakSet (e não uma flag no objeto) pra não mexer
 * no que o GTM lê do dataLayer; impede que wrapper e backfill mandem o mesmo
 * evento 2 vezes.
 */
const forwarded = new WeakSet<object>();

/**
 * Chamada pelo wrapper do dataLayer com os args de cada push e, na
 * instalação do wrapper, com a fila inteira — pra recuperar o que entrou
 * antes do hook existir (o `view_item` da carga sai no `load`, e o hook só
 * é instalado na hidratação) ou enquanto o GTM o tinha substituído
 * (Finding 3 em navTracking.ts). Seguro em qualquer ordem porque só manda
 * push que já tem `event_id`, o mesmo que o Pixel usou ou vai usar.
 *
 * **Nunca lança** (mesmo motivo do Finding 2 em navTracking.ts: um throw
 * aqui subiria pelo `sendEvent()` do call-site e quebraria o add-to-cart).
 */
export function forwardFacebookCapiEvents(args: unknown[]): void {
  try {
    if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") return;
    if (!hasCookieConsent()) return;

    for (const arg of args) {
      if (!arg || typeof arg !== "object" || forwarded.has(arg)) continue;
      const body = buildFacebookCapiEvent(arg, {
        eventSourceURL: location.href,
        userAgent: navigator.userAgent,
        email: readFbeEmail(),
        fbcCookie: getCookie("_fbc") ?? undefined,
        fbpCookie: getCookie("_fbp") ?? undefined,
      });
      if (!body) continue;

      forwarded.add(arg);
      navigator.sendBeacon(
        FACEBOOK_CAPI_EVENT_URL,
        new Blob([JSON.stringify(body)], { type: "application/json" }),
      );
    }
  } catch (error) {
    console.error("[metaCapi] falha ao encaminhar evento:", error);
  }
}
